import { getRandomInt, generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'

interface RangeModel {
  numerator: { min: number; max: number }
  denominator: { min: number; max: number }
  quotient: { min: number; max: number }
  maxHistory: number
}

interface DifficultyLevelRangeMapModel {
  [key: number]: RangeModel
}

export interface FractionModel {
  quotient?: number
  numerator: number
  denominator: number
}

export interface FractionImproperToMixedChooseQuestionModel {
  question: FractionModel
  options: FractionModel[]
  correctAnswerIndex: number
  maxHistory: number
}

export interface FractionMixedToImproperQuestionModel {
  question: FractionModel
  answer: FractionModel
  maxHistory: number
}

const DIFFICULTY_LEVEL_RANGE_MAP: DifficultyLevelRangeMapModel = {
  1: { numerator: { min: 2, max: 4 }, denominator: { min: 2, max: 5 }, quotient: { min: 1, max: 3 }, maxHistory: 2 },
  2: { numerator: { min: 2, max: 7 }, denominator: { min: 3, max: 8 }, quotient: { min: 2, max: 7 }, maxHistory: 4 },
  3: {
    numerator: { min: 2, max: 10 },
    denominator: { min: 5, max: 11 },
    quotient: { min: 2, max: 5 },
    maxHistory: 5,
  },
}

function generateMixedFraction(difficultyLevel: number, history: number[]): FractionModel {
  const { numerator, denominator } = generateDenominatorNumerator(
    DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel].numerator.min,
    DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel].numerator.max,
    DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel].denominator.min,
    DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel].denominator.max,
    history,
  )
  const quotient = getRandomInt(
    DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel].quotient.min,
    DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel].quotient.max,
  )
  return { numerator, denominator, quotient }
}

function mixedToImproper({ quotient, numerator, denominator }: FractionModel): FractionModel {
  const _numerator = (quotient ?? 1) * denominator + numerator
  const gcd = generateGCD(denominator, _numerator)
  return { numerator: _numerator / gcd, denominator: denominator / gcd }
}

function generateGCD(number1: number, number2: number): number {
  if (number1 === 0) return number2
  return generateGCD(number2 % number1, number1)
}

export function getFractionMixedToImproperChooseQuestionData(
  difficultyLevel: number,
  history: number[],
): FractionImproperToMixedChooseQuestionModel {
  const options: FractionModel[] = new Array<FractionModel>(4)
  const range = DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel]
  const question: FractionModel = generateMixedFraction(difficultyLevel, history)
  const correctOption = mixedToImproper(question)
  const denominator = correctOption.denominator
  const correctAnswerIndex = getRandomInt(0, 3)
  let optionHistory: number[] = [correctOption.numerator]
  // eslint-disable-next-line
  for (let i = 0; i < 4; i++) {
    if (correctAnswerIndex === i) {
      options[i] = correctOption
    } else {
      const numerator = getRandomInt(
        range.numerator.min,
        range.quotient.max * range.numerator.max + range.denominator.max,
        optionHistory,
      )
      options[i] = { numerator, denominator }
      optionHistory = [...optionHistory, numerator]
    }
  }
  return { question, options, correctAnswerIndex, maxHistory: range.maxHistory }
}

export function getFractionMixedToImproperQuestionData(
  difficultyLevel: number,
  history: number[],
): FractionMixedToImproperQuestionModel {
  const range = DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel]
  const question = generateMixedFraction(difficultyLevel, history)
  const answer = mixedToImproper(question)
  return { question, answer, maxHistory: range.maxHistory }
}
